import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  YouTube,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { StyledContainer, StyledPaper, StyledTextField, StyledSubmitButton } from './styles';
import { API_URL } from './config';
import axios from 'axios';
import { formatSubscriberCount } from './youtubeUtils';
import { useNavigate } from 'react-router-dom';

function SubnicheFinder() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('Single Dad');
  const [thresholdType, setThresholdType] = useState('percentage');
  const [thresholdValue, setThresholdValue] = useState('70');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const params = new URLSearchParams({
        query: query.trim(),
        thresholdType,
      });

      if (thresholdValue) {
        params.set('thresholdValue', thresholdValue);
      }

      const response = await axios.get(`${API_URL}/api/subniche-finder?${params.toString()}`);
      setResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      setError(error.message || 'Failed to search');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  const defaultThreshold = thresholdType === 'count' ? 30 : 70;
  const displayThreshold = thresholdValue || defaultThreshold;

  const handleCopyAllUrls = async () => {
    if (!results || !results.channels || results.channels.length === 0) {
      return;
    }

    const urls = results.channels
      .map((channel) => `https://www.youtube.com/channel/${channel.channelId}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(urls);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Failed to copy URLs:', error);
      setError('Failed to copy URLs to clipboard');
    }
  };

  return (
    <Container maxWidth="xl">
      <StyledContainer>
        <Box sx={{ mb: 5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                Subniche Finder
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(203, 213, 225, 0.8)',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  maxWidth: '600px',
                }}
              >
                Find channels with a high concentration of videos matching your query. Enter a search
                term and set a threshold by count or percentage.
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 3,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#fca5a5',
            }}
          >
            {error}
          </Alert>
        )}

        <StyledPaper>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={5}>
              <StyledTextField
                fullWidth
                label="Search Query"
                placeholder="e.g., Single dad"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(15, 23, 42, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(15, 23, 42, 0.7)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(15, 23, 42, 0.8)',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                    backgroundColor: 'rgba(15, 23, 42, 0.5)',
                    '& fieldset': {
                      borderColor: 'rgba(148, 163, 184, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(148, 163, 184, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6366f1',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(15, 23, 42, 0.7)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(15, 23, 42, 0.8)',
                    },
                  },
                }}
              >
                <InputLabel sx={{ color: 'rgba(203, 213, 225, 0.7)' }}>
                  Threshold Type
                </InputLabel>
                <Select
                  value={thresholdType}
                  label="Threshold Type"
                  onChange={(e) => {
                    setThresholdType(e.target.value);
                    setThresholdValue(''); // Reset threshold value when type changes
                  }}
                  disabled={loading}
                >
                  <MenuItem value="count">By Count</MenuItem>
                  <MenuItem value="percentage">By Percentage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledTextField
                fullWidth
                label={`Threshold ${thresholdType === 'count' ? '(Count)' : '(%)'}`}
                type="number"
                placeholder={defaultThreshold.toString()}
                value={thresholdValue}
                onChange={(e) => setThresholdValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(15, 23, 42, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(15, 23, 42, 0.7)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(15, 23, 42, 0.8)',
                    },
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              alignItems: 'center',
              pt: 2,
              borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            }}
          >
            <StyledSubmitButton
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                minWidth: '160px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(148, 163, 184, 0.2)',
                  color: 'rgba(203, 213, 225, 0.5)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {loading ? 'Searching...' : 'Search'}
            </StyledSubmitButton>
          </Box>
        </StyledPaper>

        {results && (
          <StyledPaper sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6">
                Results ({results.total} channels found)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Query: "${results.query}" | Threshold: ${displayThreshold} ${
                    results.thresholdType === 'count' ? 'videos' : '%'
                  }`}
                  color="primary"
                  variant="outlined"
                />
                {results.total > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyAllUrls}
                    size="small"
                  >
                    Copy All URLs
                  </Button>
                )}
              </Box>
            </Box>

            {results.total === 0 ? (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  color: '#93c5fd',
                }}
              >
                No channels found matching the criteria. Try adjusting your query or threshold.
              </Alert>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(148, 163, 184, 0.1)',
                  '& .MuiTable-root': {
                    '& .MuiTableCell-head': {
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      color: 'rgba(241, 245, 249, 0.9)',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    },
                    '& .MuiTableCell-body': {
                      borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                      color: 'rgba(241, 245, 249, 0.8)',
                    },
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    },
                  },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Channel Name</TableCell>
                      <TableCell>Subscribers</TableCell>
                      <TableCell>Total Videos</TableCell>
                      <TableCell>Matching Videos</TableCell>
                      <TableCell>Percentage</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.channels.map((channel, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/channels/${channel.channelId}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {channel.avatarUrl && (
                              <Box
                                component="img"
                                src={channel.avatarUrl}
                                alt={channel.channelName || 'Channel'}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                flex: 1,
                              }}
                            >
                              {channel.channelName || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {channel.subscriberCount
                            ? formatSubscriberCount(channel.subscriberCount)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{channel.totalVideoCount?.toLocaleString() || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                          >
                            {channel.matchingVideoCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {channel.percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open on YouTube">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `https://www.youtube.com/channel/${channel.channelId}`,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                              }}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                },
                              }}
                            >
                              <YouTube />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        )}

        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          ContentProps={{
            sx: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              color: '#f1f5f9',
              fontWeight: 500,
              borderRadius: 3,
              padding: '12px 24px',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            },
          }}
          message="All channel URLs copied to clipboard!"
        />
      </StyledContainer>
    </Container>
  );
}

export default SubnicheFinder;

